import React from "react";
import { format } from "date-fns";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { TokenRowProps } from "components/entities";
import { Button } from "components/common/Button";
import { Collapsible } from "components/common/Collapsible";
import { Tooltip } from "components/common/Tooltip";
import {
  ContextMenu,
  type ContextMenuItem,
} from "components/common/ContextMenu";
import { DatasetMetaSection } from "components/main/datasets/DatasetMetaSection";
import { RelativeDate } from "components/canvas/detailsSide/utils/RelativeDte";
import { useContextMenu } from "components/hooks/useContextMenu";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

const STATUS_DOT: Record<string, string> = {
  active: "bg-success",
  expired: "bg-fg-muted",
  revoked: "bg-error",
};

const formatFullDate = (date: Date): string =>
  date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

/// One labelled fact in the expanded body, rendered as a dataset metadata
/// section with the value in the heading's `meta` slot — the values are all one
/// line, so a `pl-16` body would just add height. The fixed-width label keeps
/// them in a column.
const TokenDetail: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => (
  <DatasetMetaSection
    icon={icon}
    label={label}
    labelClassName="text-fg-muted-semibold w-24 shrink-0"
    pr="pr-0"
    meta={<span className="min-w-0 truncate text-fg-regular">{children}</span>}
  />
);

/// One token in the list. Mirrors the Datasets row idiom — a flat `group/card`
/// row with hover-bg that expands in place into dataset-style metadata sections.
/// The row keeps only what you scan for (status, name, when it lapses); scope and
/// creation date live in the expanded body.
export const TokenRow: React.FC<TokenRowProps> = ({
  token,
  handleRevokeToken,
  expanded,
  onExpandedChange,
}) => {
  const menu = useContextMenu();

  const status = token.get_status;
  const isActive = status === "active";
  const scopeLabel = token.get_scope === "write" ? "Write" : "Read";
  // The token's org ceiling. A name only resolves for orgs the viewer still
  // belongs to; a foreign stamp shows its raw (opaque) id.
  const orgs = useOrgContextStore((s) => s.orgs);
  const tokenOrgId = token.get_org_id ?? null;
  const tokenOrg = tokenOrgId
    ? orgs.find((o) => o.org_id === tokenOrgId)
    : undefined;
  const orgLabel = tokenOrgId
    ? tokenOrg
      ? tokenOrg.is_personal
        ? "Personal"
        : tokenOrg.name
      : tokenOrgId
    : "Account-wide (predates organizations)";
  const scopeHint =
    scopeLabel === "Write"
      ? "Can read and modify your data"
      : "Can read your data only";
  // `get_expires_at` is already in milliseconds; `get_created_at` is in seconds.
  const expiresAt = Number(token.get_expires_at);
  const createdAt = Number(token.get_created_at);
  const neverExpires = expiresAt === 0;
  const expiryDate = neverExpires ? null : new Date(expiresAt);
  const dotClass = STATUS_DOT[status] ?? "bg-fg-muted";
  const tokenId = token.get_token_id;
  const description = token.get_description;

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: expanded ? "Hide details" : "See details",
      icon: <VisibilityOutlinedIcon className="icon-md" />,
      onClick: () => onExpandedChange(!expanded),
    },
    { label: "", onClick: () => {}, divider: true },
    {
      label: "Revoke",
      icon: <DeleteOutlineIcon className="icon-md" />,
      onClick: () => handleRevokeToken(tokenId),
      disabled: !isActive,
      danger: true,
    },
  ];

  return (
    <div id={`token-${tokenId}`} className="rounded-app group/card">
      <div
        className={`group flex items-center gap-2 item-height px-1 rounded-app select-none cursor-pointer group-hover/card:bg-bg-hover transition-colors ${
          isActive ? "" : "opacity-70"
        } ${menu.isOpen ? "bg-bg-hover" : ""}`}
        tabIndex={0}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest("button")) return;
          onExpandedChange(!expanded);
        }}
        {...menu.triggerProps}
      >
        <Button
          variant="default"
          size="xs"
          aria-label={
            expanded ? `Collapse ${description}` : `Expand ${description}`
          }
          onClick={() => onExpandedChange(!expanded)}
        >
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </Button>

        <span className="flex items-center justify-center w-5 shrink-0">
          <Tooltip title={status}>
            <span
              role="img"
              aria-label={`Token status: ${status}`}
              className={`w-2 h-2 rounded-full ${dotClass}`}
            />
          </Tooltip>
        </span>

        <div className="min-w-0 flex-1">
          <span
            className="block truncate text-fg"
            style={{
              fontSize: "var(--font-size)",
              fontWeight: "var(--font-weight-medium)",
            }}
          >
            {description}
          </span>
        </div>

        {/* The one field that demands action — the rest is in the body. */}
        <div className="shrink-0 hidden sm:inline-flex items-center text-fg-muted-regular-small">
          <Tooltip
            title={
              expiryDate
                ? `Expires ${formatFullDate(expiryDate)}`
                : "Never expires"
            }
          >
            <span className="inline-flex items-center gap-1">
              <AccessTimeIcon className="icon-xs text-fg-muted-medium" />
              {expiryDate ? format(expiryDate, "dd/MM/yyyy") : "No expiry"}
            </span>
          </Tooltip>
        </div>
      </div>

      <Collapsible open={expanded} className="pb-2 cursor-default">
        <TokenDetail
          icon={<VpnKeyOutlinedIcon className="icon-sm" />}
          label="Scope"
        >
          {scopeLabel}
          <span className="text-fg-muted"> · {scopeHint}</span>
        </TokenDetail>
        <TokenDetail
          icon={<CorporateFareOutlinedIcon className="icon-sm" />}
          label="Organization"
        >
          {orgLabel}
        </TokenDetail>
        <TokenDetail
          icon={<CalendarTodayIcon className="icon-sm" />}
          label="Created"
        >
          <RelativeDate date={new Date(createdAt * 1000)} showCreated />
        </TokenDetail>
        <TokenDetail
          icon={<AccessTimeIcon className="icon-sm" />}
          label="Expires"
        >
          {expiryDate ? formatFullDate(expiryDate) : "Never"}
        </TokenDetail>
      </Collapsible>

      <ContextMenu
        items={contextMenuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${description}`}
      />
    </div>
  );
};
